import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Synopsis.module.css";
import { formatDDMMYYYY } from "../../../utils/dateUtils";

export default function Synopsis({ data, hasVariations }) {
  if (!data || !data.summary) return null;

  const [expanded, setExpanded] = useState(false);
  const descriptionRef = useRef(null);
  const whatsNewRef = useRef(null);
  const navigate = useNavigate();
  const authorisedVariationRaw =
    data.summary.authorisedVariation ?? data.summary.AuthorisedVariation;
  const hasVariationsOverride = typeof hasVariations === "boolean";
  const authorisedVariation =
    hasVariationsOverride
      ? (hasVariations ? "Yes" : "No")
      : authorisedVariationRaw != null && String(authorisedVariationRaw).trim() !== ""
        ? String(authorisedVariationRaw)
        : "N/A";
  const isVariationYes = hasVariationsOverride
    ? hasVariations
    : String(authorisedVariationRaw || "")
      .trim()
      .toLowerCase() === "yes";
  const variationTooltip = isVariationYes
    ? data.summary.variation || data.summary.Variation || ""
    : "";
  const withdrawnDate = data.summary.withdrawnDate
    ? formatDDMMYYYY(data.summary.withdrawnDate)
    : "N/A";

  const processHtmlContent = (html) => {
    if (!html) return "N/A";
    return html.replace(/target="_new"/gi, 'target="_blank"');
  };

  useEffect(() => {
    const handleClick = (e) => {
      const link = e.target.closest('a');

      if (link) {
        const href = link.getAttribute('href');
        if (!href) return;

        const referenceMatch = href.match(/\/api\/v1\/files\/references\/(\d+)/);

        const documentMatch = href.match(/\/document\/(\d+)/);

        if (referenceMatch || documentMatch) {
          // console.log('Intercepted internal link:', href);

          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const documentId = referenceMatch ? referenceMatch[1] : documentMatch[1];
          // console.log('Navigating to document:', documentId);

          const nextPath = `/document/${documentId}`;
          if (link.getAttribute("target") === "_blank") {
            window.open(nextPath, "_blank", "noopener,noreferrer");
          } else {
            navigate(nextPath);
          }

          return false;
        }
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [navigate]);

  return (
    <section className={styles.synopsis}>
      <div className={styles.section}>
        <span className={styles.synTitle}>
          <h5>Summary</h5>
        </span>

        <div
          ref={descriptionRef}
          className={styles.synopsisDescription}
          dangerouslySetInnerHTML={{
            __html: processHtmlContent(data.summary.description),
          }}
        />

        <div className={styles.moreInfo}>
          <span className={styles.sectionInfo}>
            <p className={styles.sectionTitle}>Category:</p>
            <p>{data.summary.category || "N/A"}</p>
          </span>

          <span className={styles.sectionInfo}>
            <p className={styles.sectionTitle}>Document Type:</p>
            <p>{data.summary.documentType || "N/A"}</p>
          </span>

          <span className={styles.sectionInfo}>
            <p className={styles.sectionTitle}>Issue:</p>
            <p>{data.summary.issue || "N/A"}</p>
          </span>

          <span className={styles.sectionInfo}>
            <p className={styles.sectionTitle}>Level:</p>
            <p>{data.summary.level || "N/A"}</p>
          </span>

          <span className={styles.sectionInfo}>
            <p className={styles.sectionTitle}>Variations:</p>
            <p title={variationTooltip || undefined}>{authorisedVariation}</p>
          </span>

          <span className={styles.sectionInfo}>
            <p className={styles.sectionTitle}>Withdrawal Date:</p>
            <p>{withdrawnDate}</p>
          </span>
        </div>

        {!expanded && (
          <span
            className={styles.readMore}
            onClick={() => setExpanded(true)}
          >
            Read more
          </span>
        )}

        {expanded && (
          <>
            {/* <span className={styles.synTitle}>
              <h5>Context &amp; Details</h5>
            </span> */}

            <div className={styles.moreInfo}>
              <span className={styles.sectionInfo}>
                <p className={styles.sectionTitle}>Applicability:</p>
                <p>{data.applicability || "N/A"}</p>
              </span>

              <span className={styles.sectionInfo}>
                <p className={styles.sectionTitle}>Compliance Impact:</p>
                <p>{data.complianceImpact || "N/A"}</p>
              </span>

              <span className={styles.sectionInfo}>
                <p className={styles.sectionTitle}>Summary of changes:</p>

                <div
                  ref={whatsNewRef}
                  className={styles.htmlContent}
                  dangerouslySetInnerHTML={{
                    __html: processHtmlContent(data.summary.whatsNew),
                  }}
                />
              </span>
            </div>

            <span
              className={styles.readMore}
              onClick={() => setExpanded(false)}
            >
              Read less
            </span>
          </>
        )}

      </div>
    </section >
  );
}
